import React, { useState, useMemo } from "react";
import { useData, save } from "../hooks/useData";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, DataTable, Button, Modal, Field, Input, Textarea, Select } from "../components/shared";
import { money, num, fmtDate } from "../lib/api";
import { Plus, Trash2 } from "lucide-react";

export default function JobberBills() {
  const { data, loading, reload } = useData("/jobber-bills");
  const { data: issues } = useData("/process-issues");
  const { data: jobbers } = useData("/jobbers");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  // Every jobber process issue is billable — including ones already received / shifted forward
  const jobberIssues = useMemo(
    () => (issues || []).filter((i) => (i.jobber_name || i.jobber_id) && i.status !== "Joined"),
    [issues]
  );

  const blankLine = () => ({ size_group: "", quantity: "", rate: "" });
  const blankLot = () => ({ issue_id: "", challan_number: "", process: "", design_number: "", lines: [blankLine()] });
  const openNew = () => { setEditId(null); setForm({ jobber_id: "", jobber_name: "", bill_number: "", bill_date: new Date().toISOString().slice(0, 10), remarks: "", lots: [blankLot()] }); };
  const openEdit = (r) => {
    setEditId(r.id);
    const srcLots = (r.lots && r.lots.length) ? r.lots : [{ issue_id: "", challan_number: r.challan_number, process: r.process, design_number: r.design_number, size_rates: r.size_rates, quantity: r.quantity, rate: r.rate }];
    setForm({
      jobber_id: r.jobber_id || "", jobber_name: r.jobber_name || "",
      bill_number: r.bill_number || "", bill_date: (r.bill_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      remarks: r.remarks || "",
      lots: srcLots.map((lot) => ({
        issue_id: lot.issue_id || "", challan_number: lot.challan_number || "", process: lot.process || "", design_number: lot.design_number || "",
        lines: (lot.size_rates && lot.size_rates.length ? lot.size_rates : [{ size_group: "", quantity: lot.quantity, rate: lot.rate }]).map((l) => ({ size_group: l.size_group || "", quantity: l.quantity ?? "", rate: l.rate ?? "" })),
      })),
    });
  };
  const closeForm = () => { setForm(null); setEditId(null); };

  const pickIssue = (lotIdx, id) => {
    const i = jobberIssues.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      lots: f.lots.map((lot, li) => {
        if (li !== lotIdx) return lot;
        if (!i) return { ...lot, issue_id: "" };
        const lines = (i.sizes && i.sizes.length)
          ? i.sizes.filter((s) => (parseFloat(s.qty) || 0) > 0).map((s) => ({ size_group: s.size || "", quantity: s.qty, rate: i.issue_rate || "" }))
          : [{ size_group: "", quantity: i.received_qty || i.total_qty || "", rate: i.issue_rate || "" }];
        return { ...lot, issue_id: id, challan_number: i.challan_number || "", process: i.to_process || "", design_number: i.design_number || "", lines: lines.length ? lines : [blankLine()] };
      }),
    }));
  };

  const setLotField = (lotIdx, key, val) => setForm((f) => ({ ...f, lots: f.lots.map((lot, li) => li === lotIdx ? { ...lot, [key]: val } : lot) }));
  const setLine = (lotIdx, idx, key, val) => setForm((f) => ({ ...f, lots: f.lots.map((lot, li) => li !== lotIdx ? lot : { ...lot, lines: lot.lines.map((l, i) => i === idx ? { ...l, [key]: val } : l) }) }));
  const addLine = (lotIdx) => setForm((f) => ({ ...f, lots: f.lots.map((lot, li) => li === lotIdx ? { ...lot, lines: [...lot.lines, blankLine()] } : lot) }));
  const removeLine = (lotIdx, idx) => setForm((f) => ({ ...f, lots: f.lots.map((lot, li) => li !== lotIdx ? lot : { ...lot, lines: lot.lines.length > 1 ? lot.lines.filter((_, i) => i !== idx) : lot.lines }) }));
  const addLot = () => setForm((f) => ({ ...f, lots: [...f.lots, blankLot()] }));
  const removeLot = (lotIdx) => setForm((f) => ({ ...f, lots: f.lots.length > 1 ? f.lots.filter((_, i) => i !== lotIdx) : f.lots }));

  const lotQty = (lot) => (lot.lines || []).reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0);
  const lotAmt = (lot) => (lot.lines || []).reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.rate) || 0), 0);
  const grandQty = (form?.lots || []).reduce((s, lot) => s + lotQty(lot), 0);
  const grandAmt = (form?.lots || []).reduce((s, lot) => s + lotAmt(lot), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.jobber_name && !form.jobber_id) { toast.error("Select a jobber"); return; }
    const jb = (jobbers || []).find((j) => j.id === form.jobber_id);
    const lots = (form.lots || []).map((lot) => ({
      issue_id: lot.issue_id, challan_number: lot.challan_number, process: lot.process, design_number: lot.design_number,
      size_rates: (lot.lines || []).filter((l) => (parseFloat(l.quantity) || 0) > 0).map((l) => ({ size_group: l.size_group, quantity: parseFloat(l.quantity) || 0, rate: parseFloat(l.rate) || 0 })),
    })).filter((lot) => lot.size_rates.length > 0);
    if (!lots.length) { toast.error("Add at least one lot with a size-group line & quantity"); return; }
    try {
      const payload = { jobber_id: form.jobber_id, jobber_name: form.jobber_name || jb?.name, bill_number: form.bill_number, bill_date: form.bill_date, remarks: form.remarks, lots };
      if (editId) await save("put", `/jobber-bills/${editId}`, payload);
      else await save("post", "/jobber-bills", payload);
      toast.success(editId ? "Jobber bill updated" : "Jobber bill recorded"); closeForm(); reload();
    } catch { /* toast shown by save */ }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete jobber bill ${r.bill_number || ""} of ${money(r.amount)}?`)) return;
    try { await api.delete(`/jobber-bills/${r.id}`); toast.success("Jobber bill deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const rateDisplay = (r) => (r.lot_count > 1 || (r.size_rates && r.size_rates.length > 1)) ? "Mixed" : num(r.rate ?? (r.size_rates?.[0]?.rate));
  const billSummary = (r) => {
    if (r.lots && r.lots.length) {
      return r.lots.map((lot) => `${lot.design_number || lot.challan_number || "-"}: ${(lot.size_rates || []).map((l) => `${l.size_group || "-"} ${num(l.quantity)}×${num(l.rate)}`).join(", ") || `${num(lot.quantity)}×${num(lot.rate)}`}`).join("   |   ");
    }
    return (r.size_rates || []).map((l) => `${l.size_group || "-"}: ${num(l.quantity)}×${num(l.rate)}`).join("  |  ");
  };

  const columns = [
    { key: "bill_number", header: "Bill No", mono: true, render: (r) => r.bill_number || "—" },
    { key: "bill_date", header: "Date", render: (r) => fmtDate(r.bill_date) },
    { key: "jobber_name", header: "Jobber", render: (r) => <span className="font-medium text-slate-800">{r.jobber_name}</span> },
    { key: "challan_number", header: "Challan", mono: true, render: (r) => r.challan_number || "—" },
    { key: "process", header: "Process", render: (r) => r.process || "—" },
    { key: "design_number", header: "Design / Lots", render: (r) => (
      <span title={billSummary(r)}>
        {r.lot_count > 1 ? <span className="text-[10px] px-1 py-0.5 rounded bg-emerald-50 text-emerald-700">{r.lot_count} lots</span> : (r.design_number || "—")}
        {r.lot_count <= 1 && (r.size_rates?.length > 1) && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-indigo-50 text-indigo-700">{r.size_rates.length} sizes</span>}
      </span>
    ) },
    { key: "quantity", header: "Qty", align: "right", render: (r) => num(r.quantity) },
    { key: "rate", header: "Rate", align: "right", render: (r) => rateDisplay(r) },
    { key: "amount", header: "Amount", align: "right", render: (r) => money(r.amount) },
    { key: "act", header: "", sortable: false, render: (r) => (
      <div className="flex items-center gap-1">
        <button title="Edit" onClick={() => openEdit(r)} className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900" data-testid={`jb-edit-${r.id}`}>Edit</button>
        <button title="Delete" onClick={() => del(r)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`jb-delete-${r.id}`}><Trash2 size={15} /></button>
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Jobber Bills" subtitle="Record & track jobber process bills — multiple lots in one bill, per-size-group rates, and bill any past stage even after the lot moved on">
        <Button onClick={openNew} data-testid="add-jobber-bill-btn"><Plus size={15} /> Record Jobber Bill</Button>
      </PageHeader>
      {!loading && <DataTable testid="jobber-bills-table" columns={columns} rows={data} exportName="jobber-bills.csv" />}

      <Modal open={!!form} onClose={closeForm} title={editId ? "Edit Jobber Bill" : "Record Jobber Bill"} wide>
        {form && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <Field label="Jobber" required className="col-span-2">
              <Select value={form.jobber_id} onChange={(e) => { const j = (jobbers || []).find((x) => x.id === e.target.value); setForm({ ...form, jobber_id: e.target.value, jobber_name: j?.name || "" }); }} placeholder="Select jobber (one jobber per bill)"
                options={(jobbers || []).map((j) => ({ value: j.id, label: j.name }))} data-testid="jb-jobber" />
            </Field>

            <div className="col-span-2 space-y-3">
              {(form.lots || []).map((lot, li) => (
                <div key={li} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid={`jb-lot-${li}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-700">Lot {li + 1}</span>
                    <button type="button" onClick={() => removeLot(li)} className="p-1 text-slate-400 hover:text-red-600 disabled:opacity-30" disabled={(form.lots || []).length === 1} data-testid={`jb-lot-del-${li}`}><Trash2 size={15} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <Field label="Bill against Challan / Process (optional)" className="col-span-2">
                      <Select value={lot.issue_id} onChange={(e) => pickIssue(li, e.target.value)} placeholder="Pick a jobber challan — or enter manually below"
                        options={jobberIssues.map((i) => ({ value: i.id, label: `${i.challan_number || "No challan"} · ${i.jobber_name} · ${i.to_process} · ${i.design_number || ""} · ${num(i.total_qty)} pcs${(i.received_qty || 0) > 0 ? " · received" : ""}` }))} data-testid={`jb-issue-${li}`} />
                    </Field>
                    <Field label="Process / Stage"><Input value={lot.process} onChange={(e) => setLotField(li, "process", e.target.value)} placeholder="e.g. Embroidery" data-testid={`jb-process-${li}`} /></Field>
                    <Field label="Challan No"><Input value={lot.challan_number} onChange={(e) => setLotField(li, "challan_number", e.target.value)} data-testid={`jb-challan-${li}`} /></Field>
                    <Field label="Design No" className="col-span-2"><Input value={lot.design_number} onChange={(e) => setLotField(li, "design_number", e.target.value)} data-testid={`jb-design-${li}`} /></Field>
                  </div>
                  <div className="border-t border-slate-200 pt-2">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-slate-600">Rate by Size Group <span className="text-slate-400">(different rates per size group)</span></label>
                      <Button type="button" size="sm" variant="secondary" onClick={() => addLine(li)} data-testid={`jb-add-line-${li}`}><Plus size={13} /> Add Size Group</Button>
                    </div>
                    <div className="space-y-2">
                      {(lot.lines || []).map((l, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`jb-line-${li}-${i}`}>
                          <Input className="flex-1" placeholder="Size group (e.g. S-M, L-XL, or a single size)" value={l.size_group} onChange={(e) => setLine(li, i, "size_group", e.target.value)} data-testid={`jb-line-size-${li}-${i}`} />
                          <Input className="w-28" type="number" step="any" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(li, i, "quantity", e.target.value)} data-testid={`jb-line-qty-${li}-${i}`} />
                          <Input className="w-28" type="number" step="any" placeholder="Rate" value={l.rate} onChange={(e) => setLine(li, i, "rate", e.target.value)} data-testid={`jb-line-rate-${li}-${i}`} />
                          <span className="w-28 text-right text-sm text-slate-600 tabular">{money((parseFloat(l.quantity) || 0) * (parseFloat(l.rate) || 0))}</span>
                          <button type="button" onClick={() => removeLine(li, i)} className="p-1.5 text-slate-400 hover:text-red-600 disabled:opacity-30" disabled={(lot.lines || []).length === 1} data-testid={`jb-line-del-${li}-${i}`}><Trash2 size={15} /></button>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-end gap-6 mt-2 text-xs">
                      <span className="text-slate-500">Lot Qty: <b className="text-slate-800">{num(lotQty(lot))}</b></span>
                      <span className="text-slate-500">Lot Amount: <b className="text-slate-800">{money(lotAmt(lot))}</b></span>
                    </div>
                  </div>
                </div>
              ))}
              <Button type="button" variant="secondary" onClick={addLot} data-testid="jb-add-lot"><Plus size={14} /> Add Another Lot</Button>
            </div>

            <div className="col-span-2 flex justify-end gap-6 text-sm border-t border-slate-200 pt-3">
              <span className="text-slate-500">Total Qty: <b className="text-slate-800" data-testid="jb-total-qty">{num(grandQty)}</b></span>
              <span className="text-slate-500">Total Amount: <b className="text-blue-800" data-testid="jb-total-amount">{money(grandAmt)}</b></span>
            </div>

            <Field label="Bill Number"><Input value={form.bill_number} onChange={(e) => setForm({ ...form, bill_number: e.target.value })} data-testid="jb-billno" /></Field>
            <Field label="Bill Date"><Input type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} data-testid="jb-billdate" /></Field>
            <Field label="Remarks" className="col-span-2"><Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={closeForm}>Cancel</Button>
              <Button type="submit" data-testid="jb-save">{editId ? "Update Bill" : "Save Bill"}</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
